import React from 'react';

/**
 * Shared ProgressBar component
 *
 * Props:
 *  value        {number}  0-100 percentage
 *  label        {string}  optional label shown above the bar
 *  showValue    {boolean} show percentage text (default true)
 *  color        {string}  tailwind bg class — auto-derived from value if omitted
 *  height       {string}  tailwind h class (default 'h-2')
 *  className    {string}  extra wrapper classes
 *  animate      {boolean} pulse animation while loading (default false)
 */
const getAutoColor = (value) => {
  if (value >= 80) return 'bg-emerald-500';
  if (value >= 50) return 'bg-amber-500';
  return 'bg-red-500';
};

const getAutoTextColor = (value) => {
  if (value >= 80) return 'text-emerald-600';
  if (value >= 50) return 'text-amber-600';
  return 'text-red-600';
};

const ProgressBar = ({
  value = 0,
  label,
  showValue = true,
  color,
  height = 'h-2',
  className = '',
  animate = false,
}) => {
  const clamped = Math.min(Math.max(value ?? 0, 0), 100);
  const barColor = color || getAutoColor(clamped);
  const textColor = getAutoTextColor(clamped);

  return (
    <div className={`w-full ${className}`}>
      {(label || showValue) && (
        <div className="flex items-center justify-between mb-1.5">
          {label && (
            <span className="text-sm font-medium text-foreground">{label}</span>
          )}
          {showValue && (
            <span className={`text-sm font-bold ${textColor} ${!label ? 'ml-auto' : ''}`}>
              {clamped}%
            </span>
          )}
        </div>
      )}
      <div className={`w-full bg-muted rounded-full ${height} overflow-hidden`}>
        <div
          className={`h-full rounded-full transition-all duration-500 ${barColor} ${
            animate ? 'animate-pulse' : ''
          }`}
          style={{ width: `${clamped}%` }}
          role="progressbar"
          aria-valuenow={clamped}
          aria-valuemin={0}
          aria-valuemax={100}
        />
      </div>
    </div>
  );
};

export default ProgressBar;
